import type { Ref } from 'vue';

import { computed, onUnmounted, ref, watch } from 'vue';

export const FavoriteEntranceState = {
	IDLE: 'idle',
	STATIC: 'static',
	ENTRANCE: 'entrance',
	READY: 'ready'
} as const;

export type FavoriteEntranceState =
	(typeof FavoriteEntranceState)[keyof typeof FavoriteEntranceState];

export interface UseFavoriteEntranceAnimationOptions {
	isReady: Ref<boolean>;
	isFavorite: Ref<boolean>;
	entranceDurationMs?: number;
}

export interface UseFavoriteEntranceAnimationResult {
	state: Ref<FavoriteEntranceState>;
	shouldShowButton: Ref<boolean>;
	isAnimatingButton: Ref<boolean>;
	canPulse: Ref<boolean>;
}

const DEFAULT_ENTRANCE_MS = 1400;
const APPEAR_DELAY_MS = 500;

export function useFavoriteEntranceAnimation(
	options: UseFavoriteEntranceAnimationOptions
): UseFavoriteEntranceAnimationResult {
	const entranceMs = options.entranceDurationMs ?? DEFAULT_ENTRANCE_MS;

	const state = ref<FavoriteEntranceState>(FavoriteEntranceState.IDLE);
	let appearTimeoutId: ReturnType<typeof setTimeout> | undefined;
	let entranceTimeoutId: ReturnType<typeof setTimeout> | undefined;

	const hasInitialized = ref(false);

	function clearTimers() {
		if (appearTimeoutId) clearTimeout(appearTimeoutId);
		if (entranceTimeoutId) clearTimeout(entranceTimeoutId);
	}

	// Handle initial readiness and entrance schedule
	watch(
		() => options.isReady.value,
		ready => {
			if (!ready) {
				state.value = FavoriteEntranceState.IDLE;
				clearTimers();
				return;
			}
			if (hasInitialized.value) return;
			hasInitialized.value = true;

			if (options.isFavorite.value) {
				state.value = FavoriteEntranceState.STATIC;
				return;
			}

			// Schedule entrance once (appear delay + entrance duration)
			appearTimeoutId = setTimeout(() => {
				state.value = FavoriteEntranceState.ENTRANCE;
				entranceTimeoutId = setTimeout(() => {
					state.value = FavoriteEntranceState.READY;
				}, entranceMs);
			}, APPEAR_DELAY_MS);
		}
	);

	// React to favorite changes after initialized
	watch(
		() => options.isFavorite.value,
		(newFav, oldFav) => {
			if (!hasInitialized.value || !options.isReady.value) return;
			if (newFav && !oldFav) {
				state.value = FavoriteEntranceState.STATIC;
				clearTimers();
				return;
			}
			if (!newFav && oldFav) {
				// Remain visible without re-running entrance
				state.value = FavoriteEntranceState.READY;
				clearTimers();
			}
		}
	);

	const shouldShowButton = computed(() => options.isReady.value);
	const isAnimatingButton = computed(() => {
		if (!options.isReady.value) return false;
		if (options.isFavorite.value) return true;
		return (
			state.value === FavoriteEntranceState.ENTRANCE ||
			state.value === FavoriteEntranceState.READY
		);
	});

	const canPulse = computed(
		() =>
			state.value === FavoriteEntranceState.READY ||
			state.value === FavoriteEntranceState.STATIC
	);

	onUnmounted(() => {
		clearTimers();
	});

	return { state, shouldShowButton, isAnimatingButton, canPulse };
}
